// src/app/services/mbrs/cross-sheet-calc-debug.service.ts
import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { RuntimeConfigService } from '../common/runtime-config.service';

/**
 * Runtime-config-gated diagnostic logging for cross-sheet equality
 * ("value map") comparisons.
 *
 * WHY THIS EXISTS
 * ---------------
 * A cross-sheet mismatch that the user swears shouldn't be there has two
 * distinct root causes that look identical on screen:
 *
 *   • A stale (un-recomputed) compared value. The mounted editor recomputes
 *     only the CURRENT sheet's calculated totals; the OTHER sheet's value is
 *     read straight from the persisted snapshot. If that persisted total is
 *     stale (e.g. raw N8N extraction, or computed before a later edit) it
 *     differs from the value the user sees when they open that sheet.
 *     Signal: the two operands differ even after rounding to 2 d.p.
 *
 *   • A rounding artifact. Some comparators diff RAW values with a small
 *     tolerance instead of rounding to the MBRS 2-decimal display precision
 *     first, so e.g. a 2-d.p. value (1500.50) and the un-rounded extracted
 *     figure behind it (1500.504) register a 0.004 difference and flag
 *     spuriously.
 *     Signal: the two operands are equal at 2 d.p. but raw-diff > tolerance.
 *
 * This logger prints, at the exact comparison site, the raw operands, their
 * 2-d.p.-rounded forms, the raw and rounded differences, the comparator's
 * tolerance, and (when the caller can supply it) the freshly recomputed value
 * — then classifies the likely cause (stale operand vs rounding). That lets QA
 * tell the two apart in a live filing instead of guessing.
 *
 * SAFETY / USAGE
 * --------------
 * Two gates, both must pass:
 *   1. Build-time `!environment.production` — short-circuits FIRST so the rest
 *      is dead-code-eliminated from the production bundle.
 *   2. Runtime `RuntimeConfig.crossSheetCalcDebug` — flip on per-deployment via
 *      `/etc/mbrs/frontend-overrides.json` (see runtime-config docs). Off by
 *      default everywhere.
 *
 * All output is grouped per comparison and prefixed `[x-sheet]` for easy
 * console filtering.
 */
export interface CrossSheetCompareLog {
  /** Which comparator emitted this, e.g. 'equity-reconciliation:byYear'. */
  source: string;
  /** State-service key of the sheet currently mounted / being edited. */
  currentSheet: string;
  /** Field qname (or human label) on the current sheet. */
  currentField: string;
  /** State-service key of the referenced (compared) sheet. */
  compareSheet: string;
  /** Field qname (or human label) on the compared sheet. */
  compareField: string;
  /** Column label/suffix the comparison is for, when columnar. */
  column?: string;
  /** Current-sheet value as actually read (typically the live form). */
  currentRaw: unknown;
  /** Compared-sheet value as actually read (typically the persisted snapshot). */
  compareRaw: unknown;
  /** Freshly recomputed current value, when the caller has one (stale-operand probe). */
  currentRecomputed?: number | null;
  /** Freshly recomputed compare value, when the caller has one (stale-operand probe). */
  compareRecomputed?: number | null;
  /** Tolerance this comparator uses. */
  tolerance: number;
  /** Whether this comparator rounds operands to 2 d.p. BEFORE diffing. */
  rounds: boolean;
}

@Injectable({ providedIn: 'root' })
export class CrossSheetCalcDebugService {
  private cfg = inject(RuntimeConfigService);

  /** True only in a non-production build with the runtime flag enabled. */
  private isEnabled(): boolean {
    // Build-time outer gate: keeps the production bundle clean and ensures we
    // never even read the runtime signal in prod.
    if (environment.production) {
      return false;
    }
    // Runtime inner gate. Wrapped because comparators may run in unit tests
    // where RuntimeConfigService.load() has not resolved — reading the signal
    // would throw, and "not loaded" should simply mean "debug off".
    try {
      return this.cfg.crossSheetCalcDebug();
    }
    catch {
      return false;
    }
  }

  /**
   * Log one cross-sheet comparison. No-op unless both gates pass (see class
   * header). Safe to call unconditionally on every comparison.
   */
  log(info: CrossSheetCompareLog): void {
    if (!this.isEnabled()) {
      return;
    }

    const curNum = toNum(info.currentRaw);
    const cmpNum = toNum(info.compareRaw);
    const curR = round2(curNum);
    const cmpR = round2(cmpNum);

    const rawDiff = curNum !== null && cmpNum !== null ? Math.abs(curNum - cmpNum) : null;
    const roundedDiff = curR !== null && cmpR !== null ? Math.abs(curR - cmpR) : null;

    const equalAt2dp = roundedDiff !== null && roundedDiff <= 0.001;
    const wouldFlagOnRaw = rawDiff !== null && rawDiff > info.tolerance;

    let verdict = 'match';
    if (wouldFlagOnRaw && equalAt2dp) {
      verdict = 'LIKELY rounding artifact: equal at 2 d.p. but raw diff > tolerance';
    } else if (wouldFlagOnRaw && !equalAt2dp) {
      verdict = 'differs at 2 d.p. → real mismatch OR stale/un-recomputed compared value';
    }

    /* eslint-disable no-console */
    console.groupCollapsed(
      `[x-sheet] ${info.source}: ${info.currentField} ↔ ${info.compareField}` +
        `${info.column ? ' [' + info.column + ']' : ''} → ${verdict}`
    );
    console.log({
      current: {
        sheet: info.currentSheet,
        field: info.currentField,
        raw: info.currentRaw,
        rounded2dp: curR,
        recomputed: info.currentRecomputed,
      },
      compare: {
        sheet: info.compareSheet,
        field: info.compareField,
        raw: info.compareRaw,
        rounded2dp: cmpR,
        recomputed: info.compareRecomputed,
      },
      rawDifference: rawDiff,
      roundedDifference: roundedDiff,
      tolerance: info.tolerance,
      comparatorRoundsTo2dp: info.rounds,
      equalAt2dp,
      wouldFlagOnRaw,
    });
    console.groupEnd();
    /* eslint-enable no-console */
  }
}

/** Parse to a finite number or return null (mirrors the comparators' intent). */
function toNum(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : parseFloat(value as string);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Round to the 2-decimal MBRS display precision. */
function round2(value: number | null): number | null {
  return value === null ? null : Math.round(value * 100) / 100;
}
